import { ComponentType, ReactElement, SVGProps, useEffect, useRef } from 'react';
import { CloseIcon } from '../../../../assets/icons';
import { useIsDesktop } from '../../../../core/hooks';
import { tokens } from '../../../../core/theme';

const MOBILE_ICON_SIZE = 24;
const DESKTOP_ICON_SIZE = 32;

type FaqModalProps = {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  sections: string[];
  onClose: () => void;
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export const FaqModal = ({ icon: Icon, title, sections, onClose }: FaqModalProps): ReactElement => {
  const isDesktop = useIsDesktop();
  const iconSize = isDesktop ? DESKTOP_ICON_SIZE : MOBILE_ICON_SIZE;
  const panelRef = useRef<HTMLDivElement>(null);

  // Move o foco para o painel ao abrir e devolve ao elemento que abriu o modal quando desmonta.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    return () => {
      previouslyFocused?.focus();
    };
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) {
        return;
      }

      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div data-testid="faq-modal-overlay" style={styles.overlay} onClick={onClose}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={{
          ...styles.panel,
          padding: isDesktop ? tokens.spacing.large : tokens.spacing.default,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={styles.header}>
          <div style={styles.titleGroup}>
            <Icon width={iconSize} height={iconSize} aria-hidden="true" focusable="false" />
            <h2 style={styles.title}>{title}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar" style={styles.closeButton}>
            <CloseIcon aria-hidden="true" focusable="false" />
          </button>
        </div>
        <div style={styles.sections}>
          {sections.map((section, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} dangerouslySetInnerHTML={{ __html: section }} />
          ))}
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.overlay.DEFAULT,
    zIndex: tokens.zIndex.modal,
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.default,
    width: '100%',
    maxWidth: 601,
    maxHeight: '80vh',
    margin: tokens.spacing.default,
    overflowY: 'auto',
    boxSizing: 'border-box',
    backgroundColor: tokens.colors.global.white,
    borderRadius: tokens.radius.medium,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.small,
  },
  titleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing.small,
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontSize: tokens.typography.fontSize.subtitle,
    fontWeight: tokens.typography.fontWeight.semiBold,
    lineHeight: tokens.typography.lineHeight.heading,
    color: tokens.colors.global.black,
  },
  closeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.medium,
    fontSize: tokens.typography.fontSize.body,
    fontWeight: tokens.typography.fontWeight.regular,
    lineHeight: tokens.typography.lineHeight.body,
    color: tokens.colors.brand.gray.DEFAULT,
  },
};
